import * as React from 'react';

import type { RichBlock, RichInline, RichObjectKind, RichTable } from '@/mock';
import { richDocFrom } from '@/mock';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

import { CODE_LANGUAGES } from './code-languages';
import { MediaFile } from './media-file';
import { MentionChip } from './mention-chip';
import { ObjectChip } from './object-chip';

const LazyRichEditor = React.lazy(() => import('./rich-editor').then((module) => ({ default: module.RichEditor })));

const SAFE_HREF = /^(https?:|mailto:|tel:)/i;

const SAFE_FILE_HREF = /^(https?:|blob:|\/(?!\/))/i;

interface ViewActions {
  onOpenObject?: (ref: { kind: RichObjectKind; id: string }) => void;
  fileUrl?: (attachmentId: string) => string | null;
  renderObjectRef?: (ref: { kind: RichObjectKind; id: string; label: string }) => React.ReactNode;
}

const ViewActionsContext = React.createContext<ViewActions>({});

const HEADING_CLASS: Record<number, string> = {
  1: 'text-(length:--rich-h1) font-semibold leading-[1.4]',
  2: 'text-(length:--rich-h2) font-semibold leading-[1.4]',
  3: 'text-(length:--rich-h3) font-semibold leading-[1.4]',
};

const LIST_TYPES = ['bulletListItem', 'numberedListItem', 'checkListItem'] as const;

function isHighlightable(language: string): boolean {
  if (language === 'text') return false;
  return Object.entries(CODE_LANGUAGES).some(
    ([id, spec]) => id === language || ('aliases' in spec && spec.aliases.includes(language)),
  );
}

function codeText(block: RichBlock): string {
  return block.content.map((item) => (item.type === 'text' ? item.text : '')).join('');
}

function isListBlock(block: RichBlock): boolean {
  return (LIST_TYPES as readonly string[]).includes(block.type);
}

function TextView({ item }: { item: Extract<RichInline, { type: 'text' }> }) {
  const { styles } = item;
  let node: React.ReactNode = item.text;
  if (styles.code) node = <code className="rounded-sm bg-hover px-1 font-mono text-[0.9em]">{node}</code>;
  if (styles.bold) node = <strong className="font-semibold">{node}</strong>;
  if (styles.italic) node = <em>{node}</em>;
  if (styles.underline) node = <u>{node}</u>;
  if (styles.strike) node = <s>{node}</s>;
  return <>{node}</>;
}

function ObjectRefView({ props }: { props: Extract<RichInline, { type: 'objectRef' }>['props'] }) {
  const { onOpenObject, renderObjectRef } = React.useContext(ViewActionsContext);
  const custom = renderObjectRef?.(props);
  if (custom !== null && custom !== undefined) return <>{custom}</>;
  const { kind, id, label } = props;
  return <ObjectChip kind={kind} label={label} onOpen={onOpenObject ? () => onOpenObject({ kind, id }) : undefined} />;
}

function InlineView({ items }: { items: RichInline[] }) {
  return (
    <>
      {items.map((item, index) => {
        if (item.type === 'text') return <TextView key={index} item={item} />;
        if (item.type === 'mention') return <MentionChip key={index} handle={item.props.handle} />;
        if (item.type === 'objectRef') return <ObjectRefView key={index} props={item.props} />;
        if (!SAFE_HREF.test(item.href)) return <InlineView key={index} items={item.content} />;
        return (
          <a key={index} href={item.href} target="_blank" rel="noopener noreferrer" className="text-link underline underline-offset-2">
            <InlineView items={item.content} />
          </a>
        );
      })}
    </>
  );
}

function ListItemView({ block }: { block: RichBlock }) {
  const checkable = block.type === 'checkListItem';
  return (
    <li>
      <div className={cn(checkable && 'flex items-start gap-2')}>
        {checkable ? <Checkbox checked={block.props.checked === true} disabled className="mt-1" /> : null}
        <span>
          <InlineView items={block.content} />
        </span>
      </div>
      <BlocksView blocks={block.children} />
    </li>
  );
}

function ListView({ blocks }: { blocks: RichBlock[] }) {
  const type = blocks[0].type;
  const Tag = type === 'numberedListItem' ? 'ol' : 'ul';
  const marker = type === 'bulletListItem' ? 'list-disc pl-5' : type === 'numberedListItem' ? 'list-decimal pl-5' : 'list-none';
  return (
    <Tag className={marker}>
      {blocks.map((block) => (
        <ListItemView key={block.id} block={block} />
      ))}
    </Tag>
  );
}

function FileView({ block }: { block: RichBlock }) {
  const { fileUrl } = React.useContext(ViewActionsContext);
  const url = fileUrl?.(String(block.props.attachmentId ?? '')) ?? null;
  return (
    <MediaFile
      name={String(block.props.name ?? '')}
      mime={String(block.props.mime ?? '')}
      url={url !== null && SAFE_FILE_HREF.test(url) ? url : null}
      linkCard
    />
  );
}

function ColumnListView({ block }: { block: RichBlock }) {
  return (
    <div data-column-list="" className="my-2 flex gap-6">
      {block.children.map((column) => (
        <div key={column.id} data-column="" className="min-w-0 basis-0" style={{ flexGrow: Number(column.props.width) || 1 }}>
          <BlocksView blocks={column.children} />
        </div>
      ))}
    </div>
  );
}

function TableView({ table }: { table: RichTable }) {
  return (
    <div className="my-2 overflow-x-auto">
      <table data-rich-table="" className="w-full border-collapse text-sm">
        <tbody>
          {table.rows.map((cells, rowIndex) => (
            <tr key={rowIndex}>
              {cells.map((cell, columnIndex) => {
                const header = rowIndex < table.headerRows || columnIndex < table.headerCols;
                const Tag = header ? 'th' : 'td';
                return (
                  <Tag
                    key={columnIndex}
                    className={cn('min-w-24 border border-border px-3 py-1.5 text-left align-top', header && 'bg-card font-semibold')}
                  >
                    <InlineView items={cell} />
                  </Tag>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ToggleView({ block }: { block: RichBlock }) {
  return (
    <details data-toggle="" className="my-1">
      <summary className="cursor-pointer">
        <InlineView items={block.content} />
      </summary>
      <div className="pl-5">
        <BlocksView blocks={block.children} />
      </div>
    </details>
  );
}

function highlightedCode(block: RichBlock, language: string, fallback: React.ReactNode): React.ReactNode {
  return (
    <React.Suspense fallback={fallback}>
      <LazyRichEditor
        key={`${block.id}:${language}:${codeText(block)}`}
        variant="message"
        readOnly
        ariaLabel="Блок кода"
        initialValue={[block]}
      />
    </React.Suspense>
  );
}

function BlockView({ block }: { block: RichBlock }) {
  if (block.type === 'columnList') return <ColumnListView block={block} />;
  if (block.type === 'column') return <BlocksView blocks={block.children} />;
  if (block.type === 'file') return <FileView block={block} />;
  if (block.type === 'divider') return <hr className="my-3 border-border" />;
  if (block.type === 'table' && block.table !== undefined) return <TableView table={block.table} />;
  if (block.type === 'toggleListItem') return <ToggleView block={block} />;
  const level = typeof block.props.level === 'number' ? block.props.level : 1;
  const content = <InlineView items={block.content} />;
  let body: React.ReactNode;
  if (block.type === 'heading') {
    const Tag = level === 2 ? 'h2' : level === 3 ? 'h3' : 'h1';
    body = <Tag className={HEADING_CLASS[level] ?? HEADING_CLASS[1]}>{content}</Tag>;
  } else if (block.type === 'quote') {
    body = <blockquote className="border-l-2 border-border pl-3 text-foreground-muted">{content}</blockquote>;
  } else if (block.type === 'codeBlock') {
    const language = String(block.props.language ?? 'text');
    const plain = (
      <pre data-language={language} className="overflow-x-auto rounded-md bg-card p-2 font-mono text-[0.9em]">
        <code>{content}</code>
      </pre>
    );
    body = isHighlightable(language) ? highlightedCode(block, language, plain) : plain;
  } else {
    body = <p>{content}</p>;
  }
  return (
    <>
      {body}
      {block.children.length > 0 ? (
        <div className="pl-5">
          <BlocksView blocks={block.children} />
        </div>
      ) : null}
    </>
  );
}

function groupLists(blocks: RichBlock[]): RichBlock[][] {
  const groups: RichBlock[][] = [];
  for (const block of blocks) {
    const last = groups[groups.length - 1];
    const continuesList = last !== undefined && isListBlock(block) && last[0].type === block.type;
    if (continuesList) last.push(block);
    else groups.push([block]);
  }
  return groups;
}

function BlocksView({ blocks }: { blocks: RichBlock[] }) {
  return (
    <>
      {groupLists(blocks).map((group) =>
        isListBlock(group[0]) ? <ListView key={group[0].id} blocks={group} /> : <BlockView key={group[0].id} block={group[0]} />,
      )}
    </>
  );
}

export interface RichDocViewProps extends ViewActions {
  value: unknown;
  className?: string;
  ariaLabel?: string;
}

export function RichDocView({ value, className, ariaLabel, onOpenObject, fileUrl, renderObjectRef }: RichDocViewProps) {
  const doc = React.useMemo(() => richDocFrom(value), [value]);
  const actions = React.useMemo<ViewActions>(() => ({ onOpenObject, fileUrl, renderObjectRef }), [onOpenObject, fileUrl, renderObjectRef]);
  return (
    <ViewActionsContext.Provider value={actions}>
      <div
        data-rich-doc-view=""
        role={ariaLabel === undefined ? undefined : 'group'}
        aria-label={ariaLabel}
        className={cn('flex flex-col gap-1 whitespace-pre-wrap', className)}
      >
        <BlocksView blocks={doc} />
      </div>
    </ViewActionsContext.Provider>
  );
}
